import { useRef, useEffect } from 'react';
import type { RefObject } from 'react';

const HEADER_HEIGHT_COOKIE = 'header-height';

/**
 * Hook that dynamically updates the --header-height CSS variable
 * based on the actual height of the referenced element.
 *
 * Automatically responds to:
 * - Viewport changes (mobile/desktop)
 * - Banner open/close
 * - Any content changes affecting height
 *
 * Persists the height in a cookie for SSR to prevent layout shift on hard reload.
 */
export function useHeaderHeight(): RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const updateHeight = () => {
      const height = element.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
      // Persist for next server render to prevent layout shift (1 year expiry)
      const secure = window.location.protocol === 'https:' ? ';Secure' : '';
      document.cookie = `${HEADER_HEIGHT_COOKIE}=${height};path=/;max-age=31536000;SameSite=Lax${secure}`;
    };

    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    updateHeight();

    return () => { observer.disconnect(); };
  }, []);

  return ref;
}
